import { ensureFreshToken, redirectToLogin } from '@/lib/auth';

const BASE_URL = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? '/api' : '');

export interface RequestConfig extends Omit<RequestInit, 'body'> {
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined | null>;
    authenticated?: boolean;
    parseAs?: 'json' | 'text' | 'none';
}

export type ApiFieldErrorValue = string | string[];
export type ApiFieldErrors = Record<string, ApiFieldErrorValue>;

export interface ApiErrorPayload {
    description?: string;
    message?: string;
    error?: string | null;
    translationKey?: string;
    translationKeys?: {
        description?: string;
        [key: string]: unknown;
    };
    errorMeta?: Record<string, string>;
    errors?: ApiFieldErrors;
    [key: string]: unknown;
}

export class ApiClientError extends Error {
    status: number;
    payload?: ApiErrorPayload;
    recoverable: boolean;

    constructor({
        message,
        status,
        payload,
        recoverable
    }: {
        message: string;
        status: number;
        payload?: ApiErrorPayload;
        recoverable?: boolean;
    }) {
        super(message);
        this.name = 'ApiClientError';
        this.status = status;
        this.payload = payload;
        this.recoverable = recoverable ?? (status >= 500 || status === 429);
    }

    get errors(): ApiFieldErrors | undefined {
        return this.payload?.errors;
    }

    get errorMeta(): Record<string, string> | undefined {
        return this.payload?.errorMeta;
    }
}

let isRedirectingToLogin = false;

async function handleUnauthorized(): Promise<void> {
    if (isRedirectingToLogin || typeof window === 'undefined') {
        return;
    }

    isRedirectingToLogin = true;

    try {
        await redirectToLogin();
    } finally {
        isRedirectingToLogin = false;
    }
}

function buildUrl(endpoint: string, params?: RequestConfig['params']): string {
    const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';

    let target: string;

    if (isAbsoluteUrl) {
        target = endpoint;
    } else if (
        endpoint.startsWith('/api/') ||
        endpoint === '/api' ||
        endpoint.startsWith('/public/') ||
        endpoint === '/public'
    ) {
        target = endpoint;
    } else {
        const normalizedBaseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        target = `${normalizedBaseUrl}${normalizedEndpoint}`;
    }

    const url = new URL(target, baseOrigin);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.set(key, String(value));
            }
        });
    }

    return url.toString();
}

function shouldSetJsonContentType(body: unknown): boolean {
    if (body === undefined || body === null) {
        return false;
    }

    return !(
        body instanceof FormData ||
        body instanceof URLSearchParams ||
        body instanceof Blob ||
        body instanceof ArrayBuffer
    );
}

async function parseResponseBody<T>(
    response: Response,
    parseAs: NonNullable<RequestConfig['parseAs']>
): Promise<T | string | undefined> {
    if (response.status === 204 || parseAs === 'none') {
        return undefined;
    }

    if (parseAs === 'text') {
        return response.text();
    }

    const rawBody = await response.text();

    if (!rawBody) {
        return undefined;
    }

    const contentType = response.headers.get('content-type') ?? '';

    // Accept plain JSON and structured JSON suffixes such as RFC 7807 `application/problem+json`,
    // which the backend uses for error responses.
    if (!contentType.includes('application/json') && !contentType.includes('+json')) {
        throw new ApiClientError({
            message: `Expected JSON response but received ${contentType || 'unknown content type'}`,
            status: response.status,
            recoverable: response.status >= 500
        });
    }

    return JSON.parse(rawBody) as T;
}

function getErrorMessage(status: number, payload?: ApiErrorPayload): string {
    if (typeof payload?.description === 'string' && payload.description.length > 0) {
        return payload.description;
    }

    if (typeof payload?.message === 'string' && payload.message.length > 0) {
        return payload.message;
    }

    if (typeof payload?.error === 'string' && payload.error.length > 0) {
        return payload.error;
    }

    return `Request failed with ${status}`;
}

export async function apiFetch<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
        body,
        params,
        headers: customHeaders,
        authenticated = false,
        parseAs = 'json',
        credentials = 'include',
        ...restConfig
    } = config;

    const headers = new Headers(customHeaders ?? {});
    headers.set('Accept', parseAs === 'json' ? 'application/json' : '*/*');

    if (shouldSetJsonContentType(body) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    if (authenticated) {
        const token = await ensureFreshToken();

        if (token && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
        }
    }

    const url = buildUrl(endpoint, params);

    const response = await fetch(url, {
        ...restConfig,
        headers,
        credentials,
        body:
            shouldSetJsonContentType(body) && body !== undefined
                ? JSON.stringify(body)
                : body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob
                  ? body
                  : body instanceof ArrayBuffer
                    ? body
                    : undefined
    });

    let parsedBody: T | ApiErrorPayload | string | undefined;

    try {
        parsedBody = await parseResponseBody<T>(response, parseAs);
    } catch (error) {
        if (error instanceof ApiClientError) {
            throw error;
        }

        throw new ApiClientError({
            message: error instanceof Error ? error.message : 'Failed to parse response body',
            status: response.status,
            recoverable: response.status >= 500
        });
    }

    if (!response.ok) {
        const payload =
            typeof parsedBody === 'object' && parsedBody !== null ? (parsedBody as ApiErrorPayload) : undefined;

        if (response.status === 401) {
            await handleUnauthorized();
        }

        throw new ApiClientError({
            message: getErrorMessage(response.status, payload),
            status: response.status,
            payload
        });
    }

    return parsedBody as T;
}

export const api = {
    get<T>(endpoint: string, config?: Omit<RequestConfig, 'method'>) {
        return apiFetch<T>(endpoint, { ...config, method: 'GET' });
    },

    post<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) {
        return apiFetch<T>(endpoint, { ...config, method: 'POST', body });
    },

    put<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) {
        return apiFetch<T>(endpoint, { ...config, method: 'PUT', body });
    },

    patch<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) {
        return apiFetch<T>(endpoint, { ...config, method: 'PATCH', body });
    },

    delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method'>) {
        return apiFetch<T>(endpoint, { ...config, method: 'DELETE' });
    }
};
